import { useState } from 'react';
import type { Vendor, Category } from '../../../../types';
import { CategoryGlyph } from '../../../../components/primitives/CategoryGlyph';
import { legibleOn } from '../../../../utils/legibleOn';
import { isAmenityType, amenityFill } from '../../../../utils/amenities';
import { resolveGlyphKey } from '../../../../utils/glyphKey';
import shared from './shared.module.css';

interface Props {
  vendor: Vendor;
  category?: Category | undefined;
}

/**
 * Unified 120px hero for both desktop and mobile detail panels. Always
 * renders the category-color block + glyph as the baseline; if the vendor
 * has a logo URL, overlays the image (fades in once it loads, falls back
 * silently to the glyph on load error).
 */
export function VendorHero({ vendor, category }: Props) {
  // Reset load state when navigating between vendors so the glyph shows
  // again while the next logo fetches. React's documented "store info from
  // previous render" pattern — compare current src to last seen and reset.
  const [prevSrc, setPrevSrc] = useState(vendor.image);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  if (vendor.image !== prevSrc) {
    setPrevSrc(vendor.image);
    setLoaded(false);
    setFailed(false);
  }
  const hasImage = Boolean(vendor.image) && !failed;
  // Amenity hero pulls per-type fill so it matches the marker; non-amenity
  // vendors use the category color.
  const bg = isAmenityType(vendor.type)
    ? amenityFill(vendor.type)
    : category?.color ?? 'var(--color-accent-default)';
  const dataCatId = isAmenityType(vendor.type) ? vendor.type : vendor.categoryId;
  const glyphId = resolveGlyphKey(vendor.type, vendor.categoryId);
  const glyphColor = legibleOn(bg);

  const heroImageClass = [shared.heroImage, loaded && shared.heroImageVisible]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={shared.hero}
      data-cat-id={dataCatId}
      role="img"
      aria-label={hasImage ? `${vendor.name} logo` : category?.name ? `${category.name} category` : 'Vendor category'}
    >
      <CategoryGlyph
        categoryId={glyphId}
        size={64}
        color={glyphColor}
        bgColor={bg}
      />
      {hasImage && (
        <img
          className={heroImageClass}
          src={vendor.image}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}
